import { useEffect } from 'react';
import useSeo, { type SeoData } from '@/hooks/useSeo';

export default function ArtigoComparacao() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seoData: SeoData = {
    title: 'Comparação CSSBuy vs Pandabuy vs Superbuy 2026 – Qual o Melhor Agente?',
    description: 'Comparação completa entre CSSBuy, Pandabuy e Superbuy em 2026. Análise de taxas, fretes, segurança, interface e melhor uso para cada perfil de comprador internacional.',
    keywords: 'CSSBuy vs Pandabuy, CSSBuy vs Superbuy, comparação agentes China, melhor agente de compra, agentes China 2026',
    canonicalPath: '/artigo/comparacao-cssbuy-pandabuy-superbuy',
    ogType: 'article',
    ogImage: 'https://readdy.ai/api/search-image?query=Three%20smartphones%20side%20by%20side%20showing%20different%20comparison%20charts%20and%20ecommerce%20interfaces%20on%20a%20clean%20minimal%20desk%2C%20warm%20natural%20lighting%2C%20cream%20and%20beige%20background%2C%20editorial%20tech%20photography%2C%20soft%20shadows&width=1200&height=630&seq=artigo-compare-hero-seo&orientation=landscape',
    schemaJsonLd: {
      '@type': 'BlogPosting',
      headline: 'Comparação CSSBuy vs Pandabuy vs Superbuy 2026',
      description: 'Análise completa dos três principais agentes de compra da China com tabela comparativa de taxas, fretes e segurança.',
      datePublished: '2026-06-03',
      dateModified: '2026-06-17',
      author: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
      publisher: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
      inLanguage: 'pt-BR',
      isAccessibleForFree: true,
    },
  };
  useSeo(seoData);

  return (
    <main className="min-h-screen bg-background-50">
      <article className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <nav className="mb-8">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar para Home
          </a>
        </nav>

        <header className="mb-12">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold tracking-wide mb-6">
            COMPARAÇÃO
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
            Comparação CSSBuy vs Pandabuy vs Superbuy 2026
          </h1>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl">
            Análise completa dos três principais agentes de compra da China. Descubra
            qual oferece o melhor custo-benefício para o seu perfil de importação.
          </p>
          <div className="flex items-center gap-4 mt-6 text-sm text-foreground-500">
            <span>Atualizado: Junho 2026</span>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>Leitura: 12 min</span>
          </div>
        </header>

        <div className="w-full h-[320px] md:h-[420px] rounded-2xl overflow-hidden mb-14">
          <img
            src="https://readdy.ai/api/search-image?query=Three%20smartphones%20side%20by%20side%20showing%20different%20comparison%20charts%20and%20ecommerce%20interfaces%20on%20a%20clean%20minimal%20desk%2C%20warm%20natural%20lighting%2C%20cream%20and%20beige%20background%2C%20editorial%20tech%20photography%2C%20soft%20shadows&width=1200&height=630&seq=artigo-compare-hero-v2&orientation=landscape"
            alt="Comparação CSSBuy vs Pandabuy vs Superbuy"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
            Visão Geral dos Agentes
          </h2>
          <p className="text-foreground-700 leading-relaxed mb-5">
            Escolher o agente de compra certo é fundamental para o sucesso das suas
            importações da China. Cada plataforma tem suas particularidades, vantagens
            e desvantagens. O <strong>CSSBuy Spreadsheet</strong> se destaca pela
            organização e controle que oferece aos compradores.
          </p>

          <div className="overflow-x-auto rounded-xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-primary-500 text-background-50">
                  <th className="text-left px-5 py-3 font-semibold">Característica</th>
                  <th className="text-left px-5 py-3 font-semibold">CSSBuy</th>
                  <th className="text-left px-5 py-3 font-semibold">Pandabuy</th>
                  <th className="text-left px-5 py-3 font-semibold">Superbuy</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Taxa de Serviço', 'Baixa (3-5%)', 'Média (5-8%)', 'Alta (8-10%)'],
                  ['Interface', 'Média', 'Boa', 'Excelente'],
                  ['Segurança', 'Alta', 'Alta', 'Muito Alta'],
                  ['Frete Base', 'Médio', 'Baixo', 'Alto'],
                  ['Suporte PT-BR', 'Limitado', 'Bom', 'Bom'],
                  ['App Mobile', 'Sim', 'Sim', 'Sim'],
                ].map((row, i) => (
                  <tr
                    key={i}
                    className={`${
                      i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/60'
                    } border-b border-background-200/50 last:border-0`}
                  >
                    <td className="px-5 py-3 font-semibold text-foreground-900">
                      {row[0]}
                    </td>
                    <td className="px-5 py-3 text-foreground-700">{row[1]}</td>
                    <td className="px-5 py-3 text-foreground-700">{row[2]}</td>
                    <td className="px-5 py-3 text-foreground-700">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
            Qual Escolher?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                name: 'CSSBuy',
                best: 'Iniciantes e compradores focados em economia',
                desc: 'Ideal para quem está começando e quer manter custos baixos com boa organização.',
              },
              {
                name: 'Pandabuy',
                best: 'Streetwear e moda',
                desc: 'Melhor opção para quem compra roupas e tênis, com interface amigável e frete competitivo.',
              },
              {
                name: 'Superbuy',
                best: 'Compras premium',
                desc: 'Para quem prioriza segurança e qualidade de serviço acima do preço.',
              },
            ].map((agent, i) => (
              <div
                key={i}
                className="bg-background-100 rounded-xl p-5 border border-background-200/60"
              >
                <h3 className="font-heading font-bold text-lg text-primary-600 mb-2">
                  {agent.name}
                </h3>
                <p className="text-xs text-foreground-500 mb-3">
                  Melhor para: {agent.best}
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {agent.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
            Experimente o CSSBuy
          </h2>
          <p className="text-background-50/85 mb-6 max-w-md mx-auto leading-relaxed">
            Comece com o agente mais econômico e organize suas compras com o CSSBuy
            Spreadsheet.
          </p>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 rounded-full text-base font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
          >
            CSSBuy Spreadsheet
            <i className="ri-arrow-right-up-line text-lg"></i>
          </a>
        </section>

        <section className="mt-12 pt-10 border-t border-background-200/70">
          <h3 className="text-lg font-semibold text-foreground-900 mb-4">
            Leia também
          </h3>
          <div className="flex flex-wrap gap-3">
            <a
              href="/artigo/cssbuy-spreadsheet-guia-completo"
              className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
            >
              Guia Completo CSSBuy
            </a>
            <a
              href="/artigo/como-usar-cssbuy-spreadsheet"
              className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
            >
              Como Usar CSSBuy
            </a>
            <a
              href="/"
              className="px-4 py-2 bg-primary-100 hover:bg-primary-200 rounded-full text-sm text-primary-700 transition-colors font-medium"
            >
              ← Voltar para Home
            </a>
          </div>
        </section>
      </article>
    </main>
  );
}